import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Collapse, IconButton,
  MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { collection, query, where, getDocs, Timestamp, addDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { getSalesPeriodForDate, getPreviousSalesPeriod } from '../utils/salesPeriod';
import { getBusinessDate , resolveBoundaryHour } from '../utils/businessDate';
import {
  calculatePayroll, summarizePayroll, describePayroll,
  type PayrollRow, type PayrollStaff, type PaymentType, type CommissionBase,
} from '../utils/payroll';
import BackAdjustmentDialog from '../components/BackAdjustmentDialog';
import { type BackAdjustment } from '../utils/backAdjustment';
import { stripUndefined } from '../utils/firestoreSafe';

/**
 * 統合給与。
 *
 * これまで給与は Firestore 側（勤怠・バック・日払い）と
 * NightPro 側（時給・歩合・手当バック）に分かれていて、
 * どちらの画面を見ても支給額が出せなかった。
 * この画面が唯一の支給額になる。
 *
 *   支給額 = 基本給 + バック + 手当バック + 歩合 − 日払い ± 調整 − 控除
 */

const yen = (n: number) => `¥${Math.round(n).toLocaleString()}`;

const Payroll: React.FC = () => {
  const { shop, user, currentRole } = useAuth();
  const [rows, setRows] = useState<PayrollRow[]>([]);
  // バックの調整。押した行を持つ。
  const [adjustTarget, setAdjustTarget] = useState<PayrollRow | null>(null);
  const [adjustments, setAdjustments] = useState<BackAdjustment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [periodOffset, setPeriodOffset] = useState(0); // 0=当期, -1=前期
  const [open, setOpen] = useState<Set<string>>(new Set());

  const periodOptions = useMemo(() => {
    if (!shop) return null;
    const opts = {
      timezone: shop.timezone || 'Asia/Tokyo',
      boundaryHour: resolveBoundaryHour(shop),
    };
    const closing = shop.salesPeriod?.closingDay ?? 'monthEnd';
    const cur = getSalesPeriodForDate(new Date(), closing, opts);
    return periodOffset === 0 ? cur : getPreviousSalesPeriod(new Date(), closing, opts);
  }, [shop, periodOffset]);

  const load = useCallback(async () => {
    if (!shop || !periodOptions) return;
    setLoading(true);
    setError(null);
    try {
      const period = periodOptions;
      const tz = shop.timezone || 'Asia/Tokyo';
      const boundary = resolveBoundaryHour(shop);
      const startTs = Timestamp.fromDate(period.start);
      const endTs = Timestamp.fromDate(period.end);

      const [empSnap, attSnap, incSnap, paySnap, adjSnap] = await Promise.all([
        getDocs(query(collection(db, 'employees'), where('shopId', '==', shop.id), where('status', '==', 'active'))),
        getDocs(query(collection(db, 'attendances'), where('shopId', '==', shop.id),
          where('workDate', '>=', startTs), where('workDate', '<', endTs))),
        getDocs(query(collection(db, 'staffIncentives'), where('shopId', '==', shop.id),
          where('salesPeriodKey', '==', period.periodKey))),
        getDocs(query(collection(db, 'payouts'), where('shopId', '==', shop.id),
          where('salesPeriodKey', '==', period.periodKey))),
        getDocs(query(collection(db, 'backAdjustments'), where('shopId', '==', shop.id),
          where('salesPeriodKey', '==', period.periodKey))),
      ]);

      // 調整はモーダルで「すでに積まれている分」として見せる
      setAdjustments(adjSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));

      // 勤怠を人ごとにまとめる
      const att = new Map<string, { minutes: number; days: Set<string>; late: number; lateMin: number; early: number; earlyMin: number; absent: number }>();
      attSnap.docs.forEach((d) => {
        const x = d.data() as any;
        const id = x.employeeId;
        if (!id) return;
        const cur = att.get(id) ?? { minutes: 0, days: new Set<string>(), late: 0, lateMin: 0, early: 0, earlyMin: 0, absent: 0 };
        if (x.status === 'absent') { cur.absent += 1; att.set(id, cur); return; }
        cur.minutes += x.actualWorkMinutes ?? 0;
        const key = x.businessDate
          ?? (x.clockIn?.timestamp?.toDate ? getBusinessDate(x.clockIn.timestamp.toDate(), tz, boundary) : null);
        if (key) cur.days.add(key);
        if (x.isLate) { cur.late += 1; cur.lateMin += x.lateMinutes ?? 0; }
        if (x.isEarlyLeave) { cur.early += 1; cur.earlyMin += x.earlyLeaveMinutes ?? 0; }
        att.set(id, cur);
      });

      // 売上・バック
      const sal = new Map<string, { sales: number; base: number; back: number; allowanceBack: number }>();
      incSnap.docs.forEach((d) => {
        const x = d.data() as any;
        const id = x.staffId;
        if (!id || x.isShopShare || x.isUnassigned) return;
        const cur = sal.get(id) ?? { sales: 0, base: 0, back: 0, allowanceBack: 0 };
        cur.sales += x.staffSales ?? 0;
        cur.base += x.staffIncentiveBase ?? 0;
        // 累進を使う店は progressiveBackAmount が支給額。使わない店は商品バック。
        cur.back += (x.progressiveBackAmount ?? 0) || (x.backAmount ?? 0);
        cur.allowanceBack += x.breakdown?.allowanceBack ?? 0;
        sal.set(id, cur);
      });
      // 手当バックはバック本体と二重にならないよう差し引く
      sal.forEach((v) => { v.back = Math.max(0, v.back - v.allowanceBack); });

      const payouts = new Map<string, number>();
      paySnap.docs.forEach((d) => {
        const x = d.data() as any;
        if (x.status !== 'paid' || !x.staffId) return;
        payouts.set(x.staffId, (payouts.get(x.staffId) ?? 0) + (x.amount ?? 0));
      });

      const adjustments = new Map<string, number>();
      adjSnap.docs.forEach((d) => {
        const x = d.data() as any;
        if (x.reversedByAdjustmentId || !x.staffId) return;
        adjustments.set(x.staffId, (adjustments.get(x.staffId) ?? 0) + (x.delta ?? 0));
      });

      const periodDays = Math.max(1, Math.round((period.end.getTime() - period.start.getTime()) / 86400000));
      const deductLate = (shop as any).attendanceSettings?.deductLate === true;

      const built = empSnap.docs.map((d) => {
        const x = d.data() as any;
        const info = x.employmentInfo ?? {};
        const fullName = [x.personalInfo?.lastName, x.personalInfo?.firstName].filter(Boolean).join(' ').trim();
        const staff: PayrollStaff = {
          id: d.id,
          name: x.displayName?.trim() || fullName || `（名前未設定 ${x.employeeCode || d.id.slice(0, 6)}）`,
          role: x.role,
          paymentType: (info.paymentType ?? 'none') as PaymentType,
          hourlyWage: x.hourlyWage ?? info.hourlyWage,
          dailyWage: info.dailyWage,
          monthlyWage: x.monthlySalary ?? info.monthlyWage,
          commissionRate: x.commissionRate,
          commissionBase: (x.commissionBase ?? 'none') as CommissionBase,
          backEnabled: x.backEnabled !== false,
          employedDays: periodDays,
        };
        const a = att.get(d.id) ?? { minutes: 0, days: new Set<string>(), late: 0, lateMin: 0, early: 0, earlyMin: 0, absent: 0 };
        const s = sal.get(d.id) ?? { sales: 0, base: 0, back: 0, allowanceBack: 0 };

        // 遅刻控除は時間単価で計算する
        const unit = staff.paymentType === 'hourly' ? (staff.hourlyWage ?? 0)
          : staff.paymentType === 'monthly' ? Math.floor((staff.monthlyWage ?? 0) / periodDays / 8)
          : 0;
        const lateDeduction = Math.floor(((a.lateMin + a.earlyMin) / 60) * unit);

        return calculatePayroll(
          staff,
          { actualWorkMinutes: a.minutes, workedDays: a.days.size, lateCount: a.late, lateMinutes: a.lateMin,
            earlyLeaveCount: a.early, earlyLeaveMinutes: a.earlyMin, absentDays: a.absent },
          s,
          { paidOut: payouts.get(d.id) ?? 0, adjustments: adjustments.get(d.id) ?? 0, lateDeduction, other: 0 },
          { periodDays, deductLate }
        );
      });

      // 何も無い人（出勤も売上も無い）は並べない
      setRows(built.filter((r) => r.workedDays > 0 || r.grossPay !== 0 || r.paidOut > 0 || r.adjustments !== 0));
    } catch (e: any) {
      setError(e?.message ?? '給与の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [shop, periodOptions]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => summarizePayroll(rows), [rows]);
  const toggle = (id: string) => setOpen((p) => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight="bold">給与</Typography>
          <Typography variant="body2" color="text.secondary">
            {periodOptions ? `${periodOptions.label}（${periodOptions.periodKey} 締め）` : '—'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField select size="small" value={periodOffset} sx={{ width: 140 }}
            onChange={(e) => setPeriodOffset(Number(e.target.value))}>
            <MenuItem value={0}>当期</MenuItem>
            <MenuItem value={-1}>前期</MenuItem>
          </TextField>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>更新</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>これが唯一の支給額です</AlertTitle>
        基本給・バック・手当バック・歩合・日払い・調整をまとめて計算しています。
        以前は勤怠側と NightPro 側に分かれていて、どちらの画面でも支給額が出せませんでした。
      </Alert>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              {([
                ['基本給', totals.basePay], ['バック', totals.back], ['手当バック', totals.allowanceBack],
                ['歩合', totals.commission], ['支給総額', totals.grossPay],
                ['日払い済み', -totals.paidOut], ['締めで渡す', totals.netPay],
              ] as [string, number][]).map(([label, v]) => (
                <Box key={label} sx={{ minWidth: 110 }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="h6" fontWeight={label === '締めで渡す' ? 'bold' : undefined}
                    color={v < 0 ? 'text.secondary' : undefined}>
                    {yen(Math.abs(v))}
                  </Typography>
                </Box>
              ))}
              <Box sx={{ minWidth: 110 }}>
                <Typography variant="caption" color="text.secondary">人件費率（対売上）</Typography>
                <Typography variant="h6" color={totals.laborRatio !== null && totals.laborRatio > 60 ? 'warning.main' : undefined}>
                  {totals.laborRatio === null ? '—' : `${totals.laborRatio}%`}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {totals.warningCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningAmberIcon />}>
              確認が必要な項目が {totals.warningCount} 件あります。各行を開くと理由が出ます。
            </Alert>
          )}

          {rows.length === 0 ? (
            <Alert severity="info">この期間の対象者がいません。</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell>スタッフ</TableCell>
                    <TableCell align="right">出勤</TableCell>
                    <TableCell align="right">時間</TableCell>
                    <TableCell align="right">基本給</TableCell>
                    <TableCell align="right">バック</TableCell>
                    <TableCell align="right">手当</TableCell>
                    <TableCell align="right">歩合</TableCell>
                    <TableCell align="right">支給総額</TableCell>
                    <TableCell align="right">日払い</TableCell>
                    <TableCell align="right">締めで渡す</TableCell>
                    <TableCell align="center" sx={{ width: 88 }}>調整</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <React.Fragment key={r.staffId}>
                      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                        <TableCell>
                          <IconButton size="small" onClick={() => toggle(r.staffId)}>
                            {open.has(r.staffId) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <span>{r.staffName}</span>
                            {r.warnings.length > 0 && (
                              <Tooltip title={r.warnings.join(' / ')}>
                                <WarningAmberIcon fontSize="small" color="warning" />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{r.workedDays}日</TableCell>
                        <TableCell align="right">{r.workedHours}h</TableCell>
                        <TableCell align="right">{yen(r.basePay)}</TableCell>
                        <TableCell align="right">{yen(r.back)}</TableCell>
                        <TableCell align="right">{yen(r.allowanceBack)}</TableCell>
                        <TableCell align="right">{yen(r.commission)}</TableCell>
                        <TableCell align="right">{yen(r.grossPay)}</TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>
                          {r.paidOut > 0 ? `−${yen(r.paidOut)}` : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: r.netPay < 0 ? 'error.main' : undefined }}>
                          {yen(r.netPay)}
                        </TableCell>
                        <TableCell align="center">
                          {/* バックの調整。計算額と実際に払う額の両方を残す。 */}
                          <Button size="small" variant="outlined"
                            onClick={() => setAdjustTarget(r)}>
                            調整
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={12} sx={{ py: 0, bgcolor: 'grey.50' }}>
                          <Collapse in={open.has(r.staffId)} unmountOnExit>
                            <Box sx={{ py: 2, px: 1 }}>
                              <Typography variant="body2" sx={{ mb: 1 }}>{describePayroll(r)}</Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip size="small" label={`基本給の根拠: ${r.basePayBasis}`} />
                                {r.commission > 0 && <Chip size="small" label={`歩合: ${r.commissionBasis}`} />}
                                {r.laborRatio !== null && <Chip size="small" label={`人件費率 ${r.laborRatio}%`} />}
                              </Stack>
                              {r.warnings.map((w, i) => (
                                <Alert key={i} severity="warning" sx={{ mt: 1 }}>{w}</Alert>
                              ))}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
      {/* バックの調整。計算額と実際に払う額の両方を残す。 */}
      {adjustTarget && (
        <BackAdjustmentDialog
          open={!!adjustTarget}
          onClose={() => setAdjustTarget(null)}
          staffId={adjustTarget.staffId}
          staffName={adjustTarget.staffName}
          salesPeriodKey={periodOptions?.periodKey ?? ''}
          calculatedAmount={adjustTarget.back}
          existingAdjustments={adjustments.filter((a) => a.staffId === adjustTarget.staffId)}
          alreadyPaidOut={adjustTarget.paidOut}
          userRole={currentRole ?? ''}
          policy={(shop as any)?.adjustmentPolicy}
          onConfirm={async ({ kind, delta, reason }) => {
            if (!shop || !user) return;
            // 調整は積むだけ。過去の記録は書き換えない。
            await addDoc(collection(db, 'backAdjustments'), stripUndefined({
              shopId: shop.id,
              staffId: adjustTarget.staffId,
              staffName: adjustTarget.staffName,
              salesPeriodKey: periodOptions?.periodKey ?? '',
              kind,
              delta,
              reason,
              calculatedAmount: adjustTarget.back,
              adjustedBy: user.uid,
              adjustedByName: (user as any).displayName ?? '',
              adjustedByRole: currentRole ?? '',
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
            }));
            setAdjustTarget(null);
            await load();
          }}
        />
      )}
    </Box>
  );
};

export default Payroll;
